import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { DollarSign, Users, Crown, Activity, LogOut, RefreshCw, Download, Mail, MailOpen, MousePointer2, MailX, Send, Zap } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import StatCard from "@/components/StatCard";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const token = typeof window !== "undefined" ? localStorage.getItem("pbd_admin_token") : null;

  useEffect(() => {
    document.documentElement.classList.add("design-a");
    if (!token) {
      navigate("/admin", { replace: true });
      return;
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("pbd_admin_token");
        navigate("/admin", { replace: true });
        return;
      }
      setError("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/admin/logout", {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    localStorage.removeItem("pbd_admin_token");
    navigate("/admin");
  };

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("pbd_admin_token");
          navigate("/admin", { replace: true });
        }
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `patriot-bonus-drive-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("CSV download failed", e);
    }
  };

  const runFollowup = async () => {
    try {
      const { data } = await api.post(
        "/admin/run-followup",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert(`Follow-up scheduler processed ${data.processed} candidate(s).`);
      fetchStats();
    } catch (e) {
      console.error(e);
      alert("Follow-up trigger failed.");
    }
  };

  const dollars = stats ? (stats.total_raised_cents / 100).toFixed(2) : "0.00";

  return (
    <div className="design-a min-h-screen px-6 py-12 stars-bg">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between border-b border-[#ffd700]/30 pb-6 mb-10">
          <div>
            <span className="font-accent text-[10px] tracking-[0.4em] text-[#ffd700]">
              COMMAND CENTER · LIVE
            </span>
            <h1 className="mt-2 font-display text-5xl md:text-6xl font-black gold-text leading-none">
              Donation Vault
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="admin-run-followup"
              onClick={runFollowup}
              className="flex items-center gap-2 px-4 py-2 border border-[#ffd700]/50 text-[#ffd700] hover:bg-[#ffd700]/10 transition"
            >
              <Zap size={14} />
              <span className="font-accent text-[11px] tracking-[0.25em]">RUN FOLLOW-UP</span>
            </button>
            <button
              data-testid="admin-export-csv"
              onClick={downloadCsv}
              className="flex items-center gap-2 px-4 py-2 border border-[#ffd700]/50 text-[#ffd700] hover:bg-[#ffd700]/10 transition"
            >
              <Download size={14} />
              <span className="font-accent text-[11px] tracking-[0.25em]">EXPORT CSV</span>
            </button>
            <button
              data-testid="admin-refresh"
              onClick={fetchStats}
              className="flex items-center gap-2 px-4 py-2 border border-[#ffd700]/50 text-[#ffd700] hover:bg-[#ffd700]/10 transition"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="font-accent text-[11px] tracking-[0.25em]">REFRESH</span>
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-[#b22234] text-white border border-[#ffd700] hover:bg-[#8b1827] transition"
            >
              <LogOut size={14} />
              <span className="font-accent text-[11px] tracking-[0.25em]">LOG OUT</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 border border-[#b22234] bg-[#b22234]/10 text-[#ff8a99]">
            {error}
          </div>
        )}

        {/* Donation stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
          <StatCard
            testid="stat-total-raised"
            icon={DollarSign}
            label="TOTAL RAISED"
            value={`$${dollars}`}
            highlight
          />
          <StatCard
            testid="stat-paid-count"
            icon={Crown}
            label="PAID DONATIONS"
            value={stats?.paid_count ?? "—"}
          />
          <StatCard
            testid="stat-mega-count"
            icon={Zap}
            label="MEGA ($400)"
            value={stats?.mega_count ?? "—"}
          />
          <StatCard
            testid="stat-american-count"
            icon={Activity}
            label="AMERICAN ($250)"
            value={stats?.american_count ?? "—"}
          />
          <StatCard
            testid="stat-double-count"
            icon={Activity}
            label="DOUBLED ($200)"
            value={stats?.double_count ?? "—"}
          />
          <StatCard
            testid="stat-standard-count"
            icon={Users}
            label="STANDARD ($100)"
            value={stats?.standard_count ?? "—"}
          />
        </div>

        {/* Email engagement tile */}
        <div className="mt-8">
          <div className="flex items-center gap-3 mb-3">
            <Mail size={14} className="text-[#ffd700]" />
            <span className="font-accent text-[11px] tracking-[0.3em] text-white/60">
              EMAIL ENGAGEMENT (ALL-TIME)
            </span>
          </div>
          <div
            data-testid="email-engagement-tile"
            className="grid grid-cols-2 md:grid-cols-5 gap-5"
          >
            <StatCard
              testid="email-stat-sent"
              icon={Send}
              label="SENT"
              value={stats?.email_total_sent ?? "—"}
            />
            <StatCard
              testid="email-stat-delivered"
              icon={Mail}
              label="DELIVERED"
              value={stats?.email_total_delivered ?? "—"}
            />
            <StatCard
              testid="email-stat-opened"
              icon={MailOpen}
              label="OPENED"
              value={stats?.email_total_opened ?? "—"}
            />
            <StatCard
              testid="email-stat-clicked"
              icon={MousePointer2}
              label="CLICKED"
              value={stats?.email_total_clicked ?? "—"}
            />
            <StatCard
              testid="email-stat-bounced"
              icon={MailX}
              label="BOUNCED"
              value={stats?.email_total_bounced ?? "—"}
              accent="red"
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-white/40">
            <span data-testid="followup-stat-sent">
              Follow-ups sent · {stats?.followup_total_sent ?? "—"}
            </span>
            <span data-testid="custom-count-stat">
              Custom-amount donations · {stats?.custom_count ?? "—"}
            </span>
          </div>
        </div>

        {/* Transactions table */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-white">
              Recent Transactions
            </h2>
            <span className="text-[10px] tracking-[0.3em] text-white/50">
              SHOWING LAST 100
            </span>
          </div>

          <div className="border border-[#ffd700]/30 overflow-x-auto bg-[#0a1628]">
            <table className="w-full text-sm" data-testid="transactions-table">
              <thead>
                <tr className="border-b border-[#ffd700]/30 text-[10px] tracking-[0.25em] text-[#ffd700] uppercase">
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Patriot</th>
                  <th className="text-left px-4 py-3">Tier</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3 pl-6">Status</th>
                  <th className="text-left px-4 py-3">Email</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.transactions || []).map((t, i) => (
                  <tr key={i} className="border-b border-white/5 text-white/80">
                    <td className="px-4 py-3 text-xs text-white/60 whitespace-nowrap">
                      {new Date(t.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-display">
                      {t.metadata?.supporter_name || "Anonymous"}
                    </td>
                    <td className="px-4 py-3">
                      <TierBadge pkg={t.package_id} amount={t.amount} />
                    </td>
                    <td className="px-4 py-3 text-right font-display">
                      ${Number(t.amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 pl-6">
                      <StatusBadge s={t.payment_status} />
                    </td>
                    <td className="px-4 py-3">
                      <EmailFunnel t={t} />
                    </td>
                  </tr>
                ))}
                {!loading && (stats?.transactions || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-white/50">
                      No transactions yet. The eagles are waiting.
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-white/50">
                      Loading…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Link
          to="/"
          className="mt-10 inline-flex items-center gap-2 font-accent text-[11px] tracking-[0.3em] text-white/50 hover:text-[#ffd700]"
        >
          ← Back to public site
        </Link>
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    paid: { c: "bg-[#10b981]/20 text-[#6ee7b7] border-[#10b981]/50", t: "PAID" },
    initiated: { c: "bg-white/10 text-white/70 border-white/20", t: "INITIATED" },
    unpaid: { c: "bg-[#b22234]/20 text-[#ff8a99] border-[#b22234]/50", t: "UNPAID" },
  };
  const info = map[s] || { c: "bg-white/10 text-white/70 border-white/20", t: (s || "—").toUpperCase() };
  return (
    <span className={`inline-block px-2 py-1 border text-[10px] tracking-widest ${info.c}`}>
      {info.t}
    </span>
  );
}

function TierBadge({ pkg, amount }) {
  const map = {
    mega: { c: "bg-[#ffd700]/20 text-[#ffd700] border border-[#ffd700]", t: "MEGA $400" },
    american: { c: "bg-[#b22234]/30 text-[#ffd700] border border-[#ffd700]", t: "AMERICAN $250" },
    double: { c: "bg-[#ffd700]/20 text-[#ffd700]", t: "DOUBLE $200" },
    standard: { c: "bg-white/10 text-white", t: "STANDARD $100" },
    custom: { c: "bg-[#2b4c7e]/40 text-[#cbd5e1] border border-white/30", t: `CUSTOM $${Number(amount || 0).toFixed(0)}` },
  };
  const info = map[pkg] || map.standard;
  return (
    <span className={`font-accent text-[10px] tracking-[0.2em] px-2 py-1 ${info.c}`}>
      {info.t}
    </span>
  );
}

function EmailFunnel({ t }) {
  const steps = [
    { key: "sent", label: "S", on: !!t.email_sent, title: "Sent" },
    { key: "delivered", label: "D", on: !!t.email_delivered, title: "Delivered" },
    { key: "opened", label: "O", on: !!t.email_opened, title: "Opened" },
    { key: "clicked", label: "C", on: !!t.email_clicked, title: "Clicked" },
  ];
  if (t.email_bounced) {
    return (
      <span className="text-[10px] tracking-widest text-[#ff8a99]" title="Bounced">
        ✕ BOUNCED
      </span>
    );
  }
  if (!t.email_sent) return <span className="text-white/40 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1" data-testid="email-funnel">
      {steps.map((s) => (
        <span
          key={s.key}
          title={s.title}
          className={
            "inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold border " +
            (s.on
              ? "bg-[#ffd700]/20 text-[#ffd700] border-[#ffd700]/60"
              : "bg-transparent text-white/30 border-white/15")
          }
        >
          {s.label}
        </span>
      ))}
    </div>
  );
}
